/**
 * BlogTopicsManager – Admin-Oberfläche zur Verwaltung der Themen-Glossar-Beiträge.
 *
 * Themen werden via Keywords automatisch in Blog-Posts verlinkt. Pro Thema:
 *   - Titel, Slug (auto-generiert)
 *   - Anreißer
 *   - Cover-Bild (URL)
 *   - Inhalt (HTML)
 *   - Keywords (kommagetrennt) → werden in Blogs erkannt und verlinkt
 *   - Veröffentlicht ja/nein
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, Save, X, Tag, Eye, EyeOff, ExternalLink } from 'lucide-react';
import axiosInstance from '../../api/axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import AdminPageHeader from '../../components/AdminPageHeader';
import { toast } from 'sonner';

const EMPTY = {
  title: '',
  slug: '',
  excerpt: '',
  content: '',
  image: '',
  keywords: '',
  is_published: true,
  sort_order: 0,
};

const BlogTopicsManager = () => {
  const [topics, setTopics] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null); // {...topic} or null
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const fetchTopics = async () => {
    try {
      const r = await axiosInstance.get('/blog/topics?only_published=false');
      setTopics(Array.isArray(r.data) ? r.data : []);
    } catch {
      toast.error('Themen konnten nicht geladen werden');
    }
  };

  /** Upload eines Bildes und Rückgabe der Server-URL. */
  const uploadImage = async (file) => {
    if (!file) return null;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Datei zu groß (max. 5 MB)');
      return null;
    }
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axiosInstance.post('/uploads/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return r.data?.url || null;
    } catch (e) {
      toast.error('Bild-Upload fehlgeschlagen');
      return null;
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await uploadImage(file);
    if (url) {
      setForm((f) => ({ ...f, image: url }));
      toast.success('Cover-Bild hochgeladen');
    }
  };

  const handleInsertContentImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await uploadImage(file);
    if (url) {
      const snippet = `\n<img src="${url}" alt="" style="max-width:100%; height:auto; margin: 1rem 0;" />\n`;
      setForm((f) => ({ ...f, content: (f.content || '') + snippet }));
      toast.success('Bild in den Inhalt eingefügt');
    }
    // Reset input so same file can be picked again
    e.target.value = '';
  };

  useEffect(() => { fetchTopics(); }, []);

  const handleOpenNew = () => {
    setEditingTopic(null);
    setForm(EMPTY);
    setShowDialog(true);
  };

  const openEdit = (t) => {
    setEditingTopic(t);
    setForm({
      ...EMPTY,
      ...t,
      keywords: (t.keywords || []).join(', '),
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Titel ist erforderlich');
      return;
    }
    if (!form.content.trim()) {
      toast.error('Inhalt ist erforderlich');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        slug: form.slug.trim() || undefined,
        excerpt: form.excerpt.trim(),
        content: form.content,
        image: form.image.trim() || null,
        keywords: form.keywords.split(',').map(k => k.trim()).filter(Boolean),
        is_published: !!form.is_published,
        sort_order: Number(form.sort_order) || 0,
      };
      if (editingTopic) {
        await axiosInstance.put(`/blog/topics/${editingTopic.id}`, payload);
        toast.success('Thema aktualisiert');
      } else {
        await axiosInstance.post('/blog/topics', payload);
        toast.success('Thema angelegt');
      }
      setShowDialog(false);
      await fetchTopics();
    } catch (e) {
      toast.error('Speichern fehlgeschlagen: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Thema "${t.title}" wirklich löschen?\n\nAlle Links in Blog-Beiträgen werden nicht mehr funktionieren.`)) return;
    try {
      await axiosInstance.delete(`/blog/topics/${t.id}`);
      toast.success('Thema gelöscht');
      await fetchTopics();
    } catch (e) {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  const togglePublished = async (t) => {
    try {
      await axiosInstance.put(`/blog/topics/${t.id}`, { is_published: !t.is_published });
      toast.success(t.is_published ? 'Thema versteckt' : 'Thema veröffentlicht');
      await fetchTopics();
    } catch {
      toast.error('Aktion fehlgeschlagen');
    }
  };

  return (
    <div className="min-h-screen bg-[#F5EDE4]" data-testid="admin-blog-topics">
      <AdminPageHeader
        title="Themen-Glossar"
        backLink="/admin"
        backLabel="Zurück zum Dashboard"
      />

      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="bg-white border border-[#E8DCC8] p-5 mb-6">
          <p className="text-sm text-[#5D4E3C] leading-relaxed">
            <strong>So funktioniert&apos;s:</strong> Lege Themen wie &bdquo;Gips gie&szlig;en&ldquo;, &bdquo;Kerzen ziehen&ldquo; oder &bdquo;Hometraining&ldquo; an.
            Trage Keywords ein (mehrere durch Komma getrennt). Sobald diese Wörter in einem Blog-Beitrag vorkommen,
            werden sie <strong>automatisch zu deiner Themen-Detailseite verlinkt</strong> – inkl. ausführlicher Anleitung.
          </p>
        </div>

        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-[#5D4E3C]">
            {topics.length} {topics.length === 1 ? 'Thema' : 'Themen'}
          </h2>
          <Button
            onClick={handleOpenNew}
            className="bg-[#5D4E3C] hover:bg-[#3E2723] text-white"
            data-testid="topic-new-btn"
          >
            <Plus size={16} className="mr-2" /> Neues Thema
          </Button>
        </div>

        <div className="grid gap-3">
          {topics.map(t => (
            <div
              key={t.id}
              className="bg-white border border-[#E8DCC8] p-4 flex items-start gap-4"
              data-testid={`topic-row-${t.id}`}
            >
              {t.image && (
                <img src={t.image} alt={t.title} className="w-20 h-20 object-cover flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-bold text-[#3E2723] truncate">{t.title}</h3>
                    <p className="text-xs text-[#8B7355] mt-0.5">/blog/themen/{t.slug}</p>
                    {t.excerpt && (
                      <p className="text-sm text-[#5D4E3C] mt-1 line-clamp-2">{t.excerpt}</p>
                    )}
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded ${
                    t.is_published ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {t.is_published ? 'Veröffentlicht' : 'Entwurf'}
                  </span>
                </div>
                {Array.isArray(t.keywords) && t.keywords.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {t.keywords.map((k, i) => (
                      <span key={i} className="text-[10px] bg-[#F5E6D3] text-[#5D4E3C] px-2 py-0.5">
                        <Tag size={9} className="inline mr-1" />{k}
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex gap-2 mt-3 flex-wrap">
                  <Button
                    size="sm" variant="outline"
                    onClick={() => openEdit(t)}
                    data-testid={`topic-edit-${t.id}`}
                  >
                    <Edit size={14} className="mr-1" /> Bearbeiten
                  </Button>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => togglePublished(t)}
                    data-testid={`topic-toggle-${t.id}`}
                  >
                    {t.is_published ? <EyeOff size={14} className="mr-1" /> : <Eye size={14} className="mr-1" />}
                    {t.is_published ? 'Verstecken' : 'Veröffentlichen'}
                  </Button>
                  <Link
                    to={`/blog/themen/${t.slug}`}
                    target="_blank"
                    className="inline-flex items-center text-sm text-[#8B5A3C] hover:text-[#5D4E3C]"
                    data-testid={`topic-view-${t.id}`}
                  >
                    <ExternalLink size={14} className="mr-1" /> Ansehen
                  </Link>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => handleDelete(t)}
                    className="text-red-700 hover:bg-red-50"
                    data-testid={`topic-delete-${t.id}`}
                  >
                    <Trash2 size={14} className="mr-1" /> Löschen
                  </Button>
                </div>
              </div>
            </div>
          ))}
          {topics.length === 0 && (
            <div className="text-center py-10 text-[#8B7355]">
              Noch keine Themen. Lege das erste über &bdquo;Neues Thema&ldquo; an.
            </div>
          )}
        </div>
      </div>

      {/* Editor Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" style={{ backgroundColor: '#FAF5EE' }}>
          <DialogHeader>
            <DialogTitle className="text-[#5D4E3C]">
              {editingTopic ? 'Thema bearbeiten' : 'Neues Thema'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label className="text-[#5D4E3C]">Titel *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="z.B. Gips gießen – Schritt für Schritt"
                data-testid="topic-title-input"
              />
            </div>

            <div>
              <Label className="text-[#5D4E3C]">Slug (optional, sonst automatisch)</Label>
              <Input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder="gips-giessen"
                data-testid="topic-slug-input"
              />
              <p className="text-xs text-[#8B7355] mt-1">URL: /blog/themen/{form.slug || '…'}</p>
            </div>

            <div>
              <Label className="text-[#5D4E3C]">
                Keywords (kommagetrennt) *
              </Label>
              <Input
                value={form.keywords}
                onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                placeholder="Gips, Gips gießen, Gipsfigur, Gipsformen"
                data-testid="topic-keywords-input"
              />
              <p className="text-xs text-[#8B7355] mt-1">
                Diese Wörter werden in allen Blog-Beiträgen automatisch zu diesem Thema verlinkt.
              </p>
            </div>

            <div>
              <Label className="text-[#5D4E3C]">Anreißer (kurzer Text unter dem Titel)</Label>
              <Textarea
                value={form.excerpt}
                onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                rows={2}
                placeholder="Schritt für Schritt zur perfekten Gipsfigur …"
                data-testid="topic-excerpt-input"
              />
            </div>

            <div>
              <Label className="text-[#5D4E3C]">Cover-Bild</Label>
              <div className="flex flex-col sm:flex-row gap-2 mt-1">
                <Input
                  value={form.image}
                  onChange={(e) => setForm({ ...form, image: e.target.value })}
                  placeholder="https://… oder per Upload"
                  data-testid="topic-image-input"
                  className="flex-1"
                />
                <label className="inline-flex items-center justify-center bg-[#5D4E3C] text-white px-3 py-2 text-sm cursor-pointer hover:bg-[#3E2723]" data-testid="topic-cover-upload-btn">
                  Hochladen
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleCoverUpload}
                  />
                </label>
                {form.image && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setForm({ ...form, image: '' })}
                    data-testid="topic-cover-clear-btn"
                  >
                    Entfernen
                  </Button>
                )}
              </div>
              {form.image && (
                <div className="mt-2 inline-block border border-[#E8DCC8] p-1 bg-white">
                  <img src={form.image} alt="" className="max-h-40 object-cover" />
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-[#5D4E3C]">Inhalt (HTML) *</Label>
                <label className="inline-flex items-center text-xs text-[#8B5A3C] cursor-pointer hover:text-[#5D4E3C]" data-testid="topic-content-image-btn">
                  + Bild in Inhalt einfügen
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleInsertContentImage}
                  />
                </label>
              </div>
              <Textarea
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                rows={14}
                className="font-mono text-sm"
                placeholder={'<h2>Was du brauchst</h2>\n<ul>\n  <li>...</li>\n</ul>\n<h2>Schritt für Schritt</h2>\n<ol>\n  <li>...</li>\n</ol>'}
                data-testid="topic-content-input"
              />
              <p className="text-xs text-[#8B7355] mt-1">
                HTML-Tags: <code>&lt;h2&gt;</code>, <code>&lt;h3&gt;</code>, <code>&lt;p&gt;</code>, <code>&lt;ul&gt;</code>, <code>&lt;ol&gt;</code>, <code>&lt;li&gt;</code>, <code>&lt;strong&gt;</code>, <code>&lt;em&gt;</code>, <code>&lt;img&gt;</code>
              </p>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                checked={form.is_published}
                onCheckedChange={(v) => setForm({ ...form, is_published: v })}
                data-testid="topic-published-switch"
              />
              <Label className="text-[#5D4E3C]">
                {form.is_published ? 'Veröffentlicht (sichtbar)' : 'Entwurf (nicht sichtbar)'}
              </Label>
            </div>

            <div>
              <Label className="text-[#5D4E3C]">Sortierung (kleinste zuerst)</Label>
              <Input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                className="w-32"
                data-testid="topic-sort-input"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setShowDialog(false)}
              data-testid="topic-cancel-btn"
            >
              <X size={16} className="mr-1" /> Abbrechen
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#5D4E3C] hover:bg-[#3E2723] text-white"
              data-testid="topic-save-btn"
            >
              <Save size={16} className="mr-1" /> {saving ? 'Speichern…' : 'Speichern'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlogTopicsManager;
